import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const keyFeaturesOptions = [
  '360 camera', 'Air Conditioning', 'Airbags', 'Alloy Wheels', 'AM/FM Radio', 'Andriod Auto',
  'Anti-Lock Brakes', 'Armrests', 'Blind Spot Monitors', 'Bluetooth','Bullbar', 
  'Carplay', 'Cruise Control', 'Cup Holders', 'Electric Mirrors', 'Electric Windows',
  'Front Fog Lamps', 'Leather Seats', 'LED Headlights', 'Panoramic roof', 'Parking Assist',
  'Parking Sensors', 'Roof Rack', 'Sidesteps', 'Spotlight', 'Sunroof',
  'Traction Control', 'Winch', 'Xenon Lights', 'Heated Seats', 'Leather Upholstery',
  'Navigation System', 'Keyless Entry', 'Thumbstart', 'Exclusive Sound System', 
  'New Tyres', 'Voice Recognition'
];

export default function AddCar() {
  const [formData, setFormData] = useState({
    make: '',
    model: '',
    year: '',
    price: '',
    condition: '',
    engine_capacity: '',
    engine_size: '',
    custom_paper: '',
    keyfeatures: [],
    body_type: '',
    transmission: '',
    drive_train: '',
    car_history: '',
    fuel_type: '',
    color: '',
    vin: '',
    other_info: '',
    mileage: '',
    location: '',
    financing: false,
    registration: false,
  });
  const [images, setImages] = useState([]);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    const checkToken = () => {
      axios.get(`${process.env.REACT_APP_API_URL}/api/admin/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {
        localStorage.removeItem('token');
        navigate('/admin/login');
      });
    };

    checkToken();

    const inactivityTimeout = 5 * 60 * 1000;
    let inactivityTimer;

    const resetTimer = () => {
      clearTimeout(inactivityTimer);
      inactivityTimer = setTimeout(() => {
        localStorage.removeItem('token');
        navigate('/admin/login');
        alert('Logged out due to inactivity.');
      }, inactivityTimeout);
    };

    resetTimer();

    window.addEventListener('mousemove', resetTimer);
    window.addEventListener('keydown', resetTimer);

    return () => {
      clearTimeout(inactivityTimer);
      window.removeEventListener('mousemove', resetTimer);
      window.removeEventListener('keydown', resetTimer);
    };
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    try {
      const data = new FormData();
      Object.keys(formData).forEach(key => {
        if (key === 'keyfeatures') {
          data.append(key, JSON.stringify(formData[key]));
        } else if (key !== 'images') {
          data.append(key, formData[key] || '');
        }
      });
      images.forEach(file => {
        if (file instanceof File) {
          data.append('images', file);
        }
      });
      await axios.post(`${process.env.REACT_APP_API_URL}/api/cars`, data, {
        headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` },
      });
      alert('Car added successfully');
      navigate('/admin/create-listing');
    } catch (err) {
      setError('Failed to add car: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files).slice(0, 10);
    if (files.every(file => file.size <= 2 * 1024 * 1024)) {
      setImages(files);
    } else {
      alert('Each image must be under 2MB');
    }
  };

  const handleFeatureChange = (feature) => {
    setFormData(prev => ({
      ...prev,
      keyfeatures: prev.keyfeatures.includes(feature)
        ? prev.keyfeatures.filter(f => f !== feature)
        : [...prev.keyfeatures, feature]
    }));
  };

  if (!localStorage.getItem('token')) return null;

  return (
    <div className="p-8 bg-white rounded-lg shadow-md max-w-2xl mx-auto">
      <h2 className="text-2xl font-bold mb-4 font-fairplay text-black">Add New Car</h2>
      {error && <p className="text-red-600 mb-4">{error}</p>}
      <form onSubmit={handleSubmit} className="space-y-4">
        <input
          type="text"
          name="make"
          value={formData.make}
          onChange={(e) => setFormData({ ...formData, make: e.target.value })}
          placeholder="Make"
          required
          className="w-full p-2 text-black rounded border border-gray-300 font-fairplay"
        />
        <input
          type="text"
          name="model"
          value={formData.model}
          onChange={(e) => setFormData({ ...formData, model: e.target.value })}
          placeholder="Model"
          required
          className="w-full p-2 text-black rounded border border-gray-300 font-fairplay"
        />
        <input
          type="number"
          name="year"
          value={formData.year}
          onChange={(e) => setFormData({ ...formData, year: e.target.value })}
          placeholder="Year"
          required
          className="w-full p-2 text-black rounded border border-gray-300 font-fairplay"
        />
        <input
          type="number"
          name="price"
          value={formData.price}
          onChange={(e) => setFormData({ ...formData, price: e.target.value })}
          placeholder="Price (₦)"
          required
          className="w-full p-2 text-black rounded border border-gray-300 font-fairplay"
        />
        <select
          name="condition"
          value={formData.condition}
          onChange={(e) => setFormData({ ...formData, condition: e.target.value })}
          required
          className="w-full p-2 text-black rounded border border-gray-300 font-fairplay"
        >
          <option value="">Select Condition</option>
          <option value="New">New</option>
          <option value="Foreign Used">Foreign Used</option>
          <option value="Nigerian Used">Nigerian Used</option>
        </select>
        <select
          name="engine_capacity"
          value={formData.engine_capacity}
          onChange={(e) => setFormData({ ...formData, engine_capacity: e.target.value })}
          required
          className="w-full p-2 text-black rounded border border-gray-300 font-fairplay"
        >
          <option value="">Select Engine Capacity</option>
          <option value="1.4L">1.4L</option>
          <option value="1.6L">1.6L</option>
          <option value="1.8L">1.8L</option>
          <option value="2.0L">2.0L</option>
          <option value="2.2L">2.2L</option>
          <option value="2.5L">2.5L</option>
          <option value="3.0L">3.0L</option>
          <option value="3.3L">3.3L</option>
          <option value="3.5L">3.5L</option>
          <option value="3.6L">3.6L</option>
          <option value="4.0L">4.0L</option>
          <option value="4.4L">4.4L</option>
          <option value="4.5L">4.5L</option>
          <option value="5.0L">5.0L</option>
          <option value="6.0L">6.0L</option>
        </select>
        <select
          name="engine_size"
          value={formData.engine_size}
          onChange={(e) => setFormData({ ...formData, engine_size: e.target.value })}
          required
          className="w-full p-2 text-black rounded border border-gray-300 font-fairplay"
        >
          <option value="">Select Engine Size</option>
          <option value="V4">V4</option>
          <option value="V5">V5</option>
          <option value="V6">V6</option>
          <option value="V8">V8</option>
          <option value="Inline 6">Inline 6</option>
          <option value="Inline 8">Inline 8</option>
        </select>
        <select
          name="custom_paper"
          value={formData.custom_paper}
          onChange={(e) => setFormData({ ...formData, custom_paper: e.target.value })}
          required
          className="w-full p-2 text-black rounded border border-gray-300 font-fairplay"
        >
          <option value="">Select Custom Paper</option>
          <option value="Yes">Yes</option>
          <option value="No">No</option>
        </select>
        <div className="space-y-2">
          <label className="block text-black font-fairplay">Key Features</label>
          <div className="grid grid-cols-2 gap-2">
            {keyFeaturesOptions.map((feature, index) => (
              <label key={index} className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={formData.keyfeatures.includes(feature)}
                  onChange={() => handleFeatureChange(feature)}
                  className="mr-2"
                />
                {feature}
              </label>
            ))}
          </div>
        </div>
        <select
          name="body_type"
          value={formData.body_type}
          onChange={(e) => setFormData({ ...formData, body_type: e.target.value })}
          required
          className="w-full p-2 text-black rounded border border-gray-300 font-fairplay"
        >
          <option value="">Select Body Type</option>
          <option value="SUV">SUV</option>
          <option value="Sedan">Sedan</option>
          <option value="Coupe">Coupe</option>
          <option value="EV">EV</option>
          <option value="Truck">Truck</option>
        </select>
        <select
          name="drive_train"
          value={formData.drive_train}
          onChange={(e) => setFormData({ ...formData, drive_train: e.target.value })}
          required
          className="w-full p-2 text-black rounded border border-gray-300 font-fairplay"
        >
          <option value="">Select Drive Train</option>
          <option value="AWD">AWD</option>
          <option value="FWD">FWD</option>
          <option value="RWD">RWD</option>
          <option value="4WD">4WD</option>
        </select>
        <input
          type="text"
          name="transmission"
          value={formData.transmission}
          onChange={(e) => setFormData({ ...formData, transmission: e.target.value })}
          placeholder="Transmission"
          className="w-full p-2 text-black rounded border border-gray-300 font-fairplay"
        />
        <textarea
          name="car_history"
          value={formData.car_history}
          onChange={(e) => setFormData({ ...formData, car_history: e.target.value })}
          placeholder="Car History"
          className="w-full p-2 text-black rounded border border-gray-300 font-fairplay"
        />
        <input
          type="text"
          name="fuel_type"
          value={formData.fuel_type}
          onChange={(e) => setFormData({ ...formData, fuel_type: e.target.value })}
          placeholder="Fuel Type"
          className="w-full p-2 text-black rounded border border-gray-300 font-fairplay"
        />
        <input
          type="text"
          name="color"
          value={formData.color}
          onChange={(e) => setFormData({ ...formData, color: e.target.value })}
          placeholder="Color"
          className="w-full p-2 text-black rounded border border-gray-300 font-fairplay"
        />
        <textarea
          name="other_info"
          value={formData.other_info}
          onChange={(e) => setFormData({ ...formData, other_info: e.target.value })}
          placeholder="Other Info"
          className="w-full p-2 text-black rounded border border-gray-300 font-fairplay"
        />
        <input
          type="number"
          name="mileage"
          value={formData.mileage}
          onChange={(e) => setFormData({ ...formData, mileage: e.target.value })}
          placeholder="Mileage"
          className="w-full p-2 text-black rounded border border-gray-300 font-fairplay"
        />
        <input
          type="text"
          name="location"
          value={formData.location}
          onChange={(e) => setFormData({ ...formData, location: e.target.value })}
          placeholder="Location"
          className="w-full p-2 text-black rounded border border-gray-300 font-fairplay"
        />
        <div>
          <label className="block text-sm font-medium text-gray-700">Is this Car Available for Financing?</label>
          <div className="flex items-center space-x-4">
            <label className="flex items-center">
              <input
                type="radio"
                name="financing"
                value="true"
                checked={formData.financing === true}
                onChange={() => setFormData({ ...formData, financing: true })}
                className="mr-2"
              />
              Yes
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="financing"
                value="false"
                checked={formData.financing === false}
                onChange={() => setFormData({ ...formData, financing: false })}
                className="mr-2"
              />
              No
            </label>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Is the Car Registered?</label>
          <div className="flex items-center space-x-4">
            <label className="flex items-center">
              <input
                type="radio"
                name="registration"
                value="true"
                checked={formData.registration === true}
                onChange={() => setFormData({ ...formData, registration: true })}
                className="mr-2"
              />
              Yes
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="registration"
                value="false"
                checked={formData.registration === false}
                onChange={() => setFormData({ ...formData, registration: false })}
                className="mr-2"
              />
              No
            </label>
          </div>
        </div>
        <div>
          <label className="block text-black font-fairplay">Upload Images (max 10, 2MB each)</label>
          <input
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileChange}
            className="w-full p-2"
          />
        </div>
        <button
          type="submit"
          className="bg-white text-black px-4 py-2 rounded border border-cyan hover:border-magenta hover:scale-105 transition-all shadow-sm font-fairplay"
        >
          Add Car
        </button>
      </form>
    </div>
  );
}
